import React from 'react';
import classnames from 'classnames';
import TableContent from 'src/components/table-content';
import { formatDate } from 'src/utils/helpers';
import AvatarDescription from 'src/components/avatar-description';
import { Row, Col, Tooltip, Divider } from 'antd';
import {useTranslation} from 'react-i18next'
import OptMatchImg from "src/assets/img/optmatch.svg";
import './style.scss';

/**
 * PeopleItem component - used in fit, match and delete modals.
 * 
 * @param {Object} props - Props for the PeopleItem component.
 * @param {Object} props.need - Deallocation item with all necessary attributes.
 * @param {string} [props.className] - Text for additional classNames for styling purposes.
 * @returns {JSX.Element} The PeopleItem component.
 * @example
 *  <PeopleItem people={deallocationObject} />
 */
function PeopleItem({ people, className }) {
    const {
        responsible_deallocation,
        collaborator_deallocation,
        createdAt,
        suggestion,
        expiresAt
    } = people;

    const { t } = useTranslation()

	const classNames = classnames('people-item', className);

    return (
        <div className={classNames} aria-label={t('profile')} tabIndex={0} >
            <Col type="flex" justify="center" className="col-padding">
                <Row type="flex">
                    <Col>
                        <div aria-describedby='profile' tabIndex={0}>
                            <AvatarDescription
                                mainText={collaborator_deallocation.login}
                                name={collaborator_deallocation?.name.toLowerCase() !== 'not found' 
                                    ? collaborator_deallocation.name 
                                    : ''
                                }
                                mainTextAriaLabel={collaborator_deallocation.login}
                                mainTextTranslation={t('name')}
                                subText={
                                    [
                                        {
                                            description: t('role'), 
                                            value: collaborator_deallocation.role
                                        }, 
                                        {
                                            description: t('positionMap'),
                                            value: collaborator_deallocation.positionMap
                                        }
                                    ]
                                }
                                expiresAt={expiresAt}
                            />
                        </div>
                        { suggestion?.optMatchPercent && 
                        <Tooltip title={t('optMatchTooltip')}>
                            <div className="optmatch">
                                <img src={OptMatchImg} alt="logo" className="optmatch--img" />
                                <span className="optmatch-text--percent text-uppercase">{suggestion.optMatchPercent} {t('fit')}</span>
                                <span className="optmatch-text--date">{t('suggestionsOptMatch')} {suggestion.matchDateSuggestion} (GMT-3)</span>
                            </div>
                        </Tooltip>
                    }
                    </Col>
                </Row>
                <Divider className="divider" />
                <div className="info">
                    <Row gutter={[0, 16]}>
                        <Col span={12}>
                            <div aria-describedby='gu' tabIndex={0}>
                                <TableContent
                                    title="GU"
                                    mainContent={collaborator_deallocation.gu}
                                    subContent={collaborator_deallocation.alliance}
                                    mainContentClass="bold"
                                />
                            </div>
                            
                        </Col>
                        <Col span={12}>
                            <div aria-describedby='responsible' tabIndex={0}>
                                <TableContent
                                    title={t('responsibleTitle')}
                                    mainContent={responsible_deallocation.name}
                                    mainContentClass="bold"
                                    subContent={`${t('createdThisPeopleOn')} ${formatDate(createdAt)}`}
                                />
                            </div>
                            
                        </Col>
                    </Row>
                    <Row>
                        <Col span={12}>
                            <div aria-describedby='location' tabIndex={0}>
                                <TableContent
                                    title={t('location')}
                                    mainContent={collaborator_deallocation.site}
                                    mainContentClass="bold"
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </Col>
        </div>
    );
}

PeopleItem.defaultProps = {
    people: '',
	className: '',
};

export default PeopleItem;
